import { useParams } from "react-router-dom";
import { useSeo } from "@/lib/seo";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { RerunPanel } from "@/components/RerunPanel";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const CELL = { pass: ["p", "✓"], fail: ["f", "✕"], blocked: ["b", "!"], not_run: ["n", "–"] };

export default function Report() {
  useSeo({ title: "Test report", noindex: true });
  const { token } = useParams();
  const { data, isLoading } = useQuery({ queryKey: ["report", token], queryFn: () => api.status(token) });
  if (isLoading) return <div className="app-main"><div className="wrap"><p>Loading report…</p></div></div>;
  const model = data?.report;
  if (!data?.report_available || !model) {
    return <div className="app-main"><div className="wrap"><h1>Report not ready</h1>
      <p className="lede">Your report appears here once released. <a className="btn" href={`/status/${token}`}>Back to status</a></p></div></div>;
  }
  return (
    <>
      <header className="site-header"><div className="wrap"><a className="logo" href="/"><i></i>99tests</a>
        <nav><span className="muted small">Report {model.jobCode}</span></nav></div></header>
      <main className="app-main"><div className="wrap" data-testid="report-view">
        <span className="pill">Delivered</span>
        <h1 style={{ fontSize: "clamp(30px,4vw,46px)", marginTop: 12 }}>{model.appLabel}</h1>
        <div className="meta"><span>Job <b>{model.jobCode}</b></span><span>Package <b>{model.packageName}</b></span>
          <span>Rubric <b>{model.rubricVersion}</b></span><span>WorkRank <b data-testid="report-workrank">{model.workrank}</b>/100</span>
          <a className="btn sm" data-testid="report-pdf-btn" href={`${BACKEND_URL}/api/report/${token}/pdf`}>Download PDF</a></div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 12, margin: "0 0 40px" }}>
          <div className="kpi"><b>{model.verdict.casesRun}</b><span>cases run</span></div>
          <div className="kpi"><b style={{ color: "var(--green)" }}>{model.verdict.passAll}</b><span>pass on all</span></div>
          <div className="kpi"><b style={{ color: "var(--fail)" }}>{model.verdict.failAny}</b><span>fail on ≥1</span></div>
          <div className="kpi"><b style={{ color: "var(--blocked)" }}>{model.verdict.blocked}</b><span>blocked</span></div>
          <div className="kpi"><b>{model.verdict.notRun}</b><span>not run</span></div>
        </div>

        <h2 style={{ fontSize: 30, marginBottom: 14 }}>Coverage</h2>
        <div className="panel" style={{ overflow: "auto", marginBottom: 40 }}>
          <table className="tbl" style={{ fontSize: 14 }}>
            <thead><tr><th>Test case</th>{model.columns.map((c) => <th key={c.key} className="c">{c.label}</th>)}</tr></thead>
            <tbody>{model.coverage.map((row) => (
              <tr key={row.caseId}><td>{row.title}<br /><small className="muted">{row.caseId} · {row.priority}</small></td>
                {row.cells.map((cell, i) => { const [cls, sym] = CELL[cell.result] || ["n", "–"]; return <td key={i} className="c"><span className={`cell ${cls}`}>{sym}</span></td>; })}
              </tr>
            ))}</tbody>
          </table>
        </div>

        <h2 style={{ fontSize: 30, marginBottom: 14 }}>Findings <span className="muted small">{model.verification.accepted} accepted · {model.verification.merged} merged · {model.verification.rejected} rejected · {model.verification.flagged} flagged</span></h2>
        {model.findings.length === 0 && <div className="empty"><b>No accepted findings</b>Everything passed verification.</div>}
        {model.findings.map((f) => (
          <article key={f.id} className="panel-soft" style={{ padding: "22px 24px", marginBottom: 14 }} data-testid="report-finding">
            <div><span className="sev" style={{ background: sevColor(f.severityVerified || f.severityReported) }}>{f.severityVerified || f.severityReported}</span>{f.caseRef && <span className="tag" style={{ marginLeft: 8 }}>{f.caseRef}</span>}</div>
            <div style={{ fontFamily: "var(--display)", fontSize: 21, fontWeight: 700, margin: "8px 0 12px" }}>{f.title}</div>
            <dl style={{ display: "grid", gridTemplateColumns: "110px 1fr", gap: "8px 14px", margin: 0, fontSize: 15 }}>
              <dt className="muted">Steps</dt><dd>{f.steps}</dd>
              <dt className="muted">Expected</dt><dd>{f.expected}</dd>
              <dt className="muted">Actual</dt><dd>{f.actual}</dd>
            </dl>
            {f.media?.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 14 }} data-testid="finding-media">
                {f.media.map((m) => m.content_type?.startsWith("video/")
                  ? <video key={m.id} src={`${BACKEND_URL}${m.url}`} controls preload="metadata" style={{ maxWidth: 320, maxHeight: 240, borderRadius: 8, border: "1px solid var(--rule)" }} />
                  : <a key={m.id} href={`${BACKEND_URL}${m.url}`} target="_blank" rel="noreferrer"><img src={`${BACKEND_URL}${m.url}`} alt={m.name} style={{ maxWidth: 200, maxHeight: 200, borderRadius: 8, border: "1px solid var(--rule)", objectFit: "cover" }} /></a>)}
              </div>
            )}
            <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid var(--rule)", fontSize: 13, color: "var(--muted)" }}>Verified: {f.verifyReason} ({f.verifyConfidence})</div>
          </article>
        ))}
        <RerunPanel token={token} jobCode={model.jobCode} />
      </div></main>
    </>
  );
}

function sevColor(s) { return { S1: "var(--fail)", S2: "#E0602E", S3: "var(--blocked)", S4: "var(--notrun)" }[s] || "var(--fail)"; }
